import { omit, pick } from "lodash";
import { isEvaluable } from "@next-core/cook";
import { hasOwnProperty, isObject } from "@next-core/utils/general";
import { transformAndInject, transform, inject } from "@next-core/inject";
import { asyncEvaluate, isPreEvaluated, evaluate, shouldDismissMarkingComputed } from "./evaluate.js";
import { getNextStateOfUseBrick, isLazyContentInUseBrick } from "./getNextStateOfUseBrick.js";
import { hasBeenComputed, markAsComputed } from "./markAsComputed.js";
import { _internalApiGetRuntimeContext } from "../Runtime.js";
const penetrableCtxNames = ["app", "location", "query", "match", "flags", "sys"];
export async function asyncComputeRealValue(value, runtimeContext) {
  let internalOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const preEvaluated = isPreEvaluated(value);
  if (preEvaluated || typeof value === "string") {
    var _runtimeContext$app;
    // For `useBrick`, some fields such as `properties`/`transform`/`events`/`if`,
    // are kept and to be evaluated later.
    const lazy = internalOptions.$$lazyForUseBrick && isLazyContentInUseBrick(internalOptions.$$stateOfUseBrick);
    let result;
    let dismissMarkingComputed = false;
    if (preEvaluated || isEvaluable(value)) {
      result = await asyncEvaluate(value, runtimeContext, {
        lazy
      });
      dismissMarkingComputed = shouldDismissMarkingComputed(value);
    } else if (lazy || (_runtimeContext$app = runtimeContext.app) !== null && _runtimeContext$app !== void 0 && _runtimeContext$app.noPlaceholders) {
      result = value;
      dismissMarkingComputed = true;
    } else {
      const penetrableCtx = runtimeContext.unsafe_penetrate ? {
        ...pick(_internalApiGetRuntimeContext(), penetrableCtxNames),
        ...omit(runtimeContext, penetrableCtxNames)
      } : runtimeContext;
      result = (hasOwnProperty(penetrableCtx, "data") ? internalOptions.noInject ? transform : transformAndInject : internalOptions.noInject ? identity : inject)(value, penetrableCtx);
    }
    if (!dismissMarkingComputed) {
      markAsComputed(result);
    }
    return result;
  }
  if (!isObject(value) || hasBeenComputed(value) || typeof value === "function") {
    return value;
  }
  if (Array.isArray(value)) {
    const nextOptions = getNextComputeOptions(internalOptions, true);
    return Promise.all(value.map(v => asyncComputeRealValue(v, runtimeContext, nextOptions)));
  }
  return Object.fromEntries((await Promise.all(Object.entries(value).map(_ref => {
    let [k, v] = _ref;
    return Promise.all([asyncComputeRealValue(k, runtimeContext), asyncComputeRealValue(v, runtimeContext, getNextComputeOptions(internalOptions, false, k))]);
  }))).concat(internalOptions.ignoreSymbols ? [] : Object.getOwnPropertySymbols(value).map(k => [k, value[k]])));
}
export function computeRealValue(value, runtimeContext) {
  let internalOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const preEvaluated = isPreEvaluated(value);
  if (preEvaluated || typeof value === "string") {
    var _runtimeContext$app2;
    // For `useBrick`, some fields such as `properties`/`transform`/`events`/`if`,
    // are kept and to be evaluated later.
    const lazy = internalOptions.$$lazyForUseBrick && isLazyContentInUseBrick(internalOptions.$$stateOfUseBrick);
    let result;
    let dismissMarkingComputed = false;
    if (preEvaluated || isEvaluable(value)) {
      result = evaluate(value, runtimeContext);
      dismissMarkingComputed = shouldDismissMarkingComputed(value);
    } else if (lazy || (_runtimeContext$app2 = runtimeContext.app) !== null && _runtimeContext$app2 !== void 0 && _runtimeContext$app2.noPlaceholders) {
      result = value;
      dismissMarkingComputed = true;
    } else {
      const penetrableCtx = runtimeContext.unsafe_penetrate ? {
        ...pick(_internalApiGetRuntimeContext(), penetrableCtxNames),
        ...omit(runtimeContext, penetrableCtxNames)
      } : runtimeContext;
      result = (hasOwnProperty(penetrableCtx, "data") ? internalOptions.noInject ? transform : transformAndInject : internalOptions.noInject ? identity : inject)(value, penetrableCtx);
    }
    if (!dismissMarkingComputed) {
      markAsComputed(result);
    }
    return result;
  }
  if (!isObject(value) || hasBeenComputed(value) || typeof value === "function") {
    return value;
  }
  if (Array.isArray(value)) {
    const nextOptions = getNextComputeOptions(internalOptions, true);
    return value.map(v => computeRealValue(v, runtimeContext, nextOptions));
  }
  return Object.fromEntries(Object.entries(value).map(_ref2 => {
    let [k, v] = _ref2;
    return [computeRealValue(k, runtimeContext), computeRealValue(v, runtimeContext, getNextComputeOptions(internalOptions, false, k))];
  }).concat(internalOptions.ignoreSymbols ? [] : Object.getOwnPropertySymbols(value).map(k => [k, value[k]])));
}
function getNextComputeOptions(internalOptions, isArray, key) {
  return internalOptions.$$lazyForUseBrick ? {
    ...internalOptions,
    $$stateOfUseBrick: getNextStateOfUseBrick(internalOptions.$$stateOfUseBrick, isArray, key)
  } : internalOptions;
}
function identity(value) {
  return value;
}
//# sourceMappingURL=computeRealValue.js.map